import React, { useEffect, useState, useMemo } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Video, Plus, Trash2, Calendar, ExternalLink, RefreshCw, Loader2, Square, Star, MessageSquare, History } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { useSubmitting } from "@/lib/useSubmitting";

const ROLE_NICE = (m) => m.scheduler_role_label || ROLE_LABELS[m.scheduler_role] || m.scheduler_role || "";

export default function MeetingsPage() {
  const { user, customRole } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("upcoming"); // 'upcoming' | 'history'
  const [show, setShow] = useState(false);
  const [joiningId, setJoiningId] = useState(null);
  const [rateFor, setRateFor] = useState(null); // meeting object to rate
  const [myRated, setMyRated] = useState(() => new Set(JSON.parse(localStorage.getItem("mtg_rated") || "[]")));
  const [form, setForm] = useState({ title: "", description: "", scheduled_at: "", duration_min: 30, participants: [] });
  const meetSubmit = useSubmitting();

  const load = async () => {
    const [m, u, attendees, h] = await Promise.all([
      api.get("/meetings"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/users/meeting-attendees").catch(() => ({ data: [] })),
      api.get("/meetings/history").catch(() => ({ data: [] })),
    ]);
    setMeetings(m.data || []);
    setHistory(h.data || []);
    setUsers(attendees.data?.length ? attendees.data : (u.data || []));
  };
  useEffect(() => { load(); }, []);

  // Auto-prompt: any ended meeting I haven't rated yet → show the rating modal
  // Rules (to stop the "10 popups in a row" glitch):
  //  1. Only prompt for meetings that ended within the last 24 hours.
  //  2. Only ONCE per calendar day per user (localStorage key `mtg_prompt_day`).
  //  3. Only when this page is visited — parent guard already ensures that.
  useEffect(() => {
    if (rateFor) return;
    const today = new Date().toISOString().slice(0, 10);
    const lastPromptDay = localStorage.getItem("mtg_prompt_day");
    if (lastPromptDay === today) return; // already prompted today
    const now = Date.now();
    const dayAgo = now - 24 * 60 * 60 * 1000;
    const pending = history.find((m) => {
      if (!(m.participants || []).includes(user.user_id)) return false;
      if (myRated.has(m.meeting_id)) return false;
      // Prefer ended_at, fall back to scheduled_at
      const endedIso = m.ended_at || m.scheduled_at;
      if (!endedIso) return false;
      const t = new Date(endedIso).getTime();
      if (isNaN(t)) return false;
      return t >= dayAgo && t <= now;
    });
    if (pending) {
      setRateFor(pending);
      localStorage.setItem("mtg_prompt_day", today);
    }
  }, [history, rateFor, user.user_id, myRated]);

  const eligible = useMemo(() => users.filter((u) => u.user_id !== user.user_id), [users, user.user_id]);

  const togglePart = (uid) => {
    setForm((f) => ({ ...f, participants: f.participants.includes(uid) ? f.participants.filter((x) => x !== uid) : [...f.participants, uid] }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (form.participants.length === 0) return toast.error("Pick at least one participant");
    return meetSubmit.run(async () => {
      try {
        await api.post("/meetings", form);
        toast.success("Meeting scheduled");
        setShow(false);
        setForm({ title: "", description: "", scheduled_at: "", duration_min: 30, participants: [] });
        load();
      } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    });
  };

  const cancel = async (m) => {
    if (!window.confirm("Cancel this meeting?")) return;
    await api.delete(`/meetings/${m.meeting_id}`);
    load();
  };

  const end = async (m) => {
    if (!window.confirm("End this meeting now? It will move to History and everyone will be asked to rate the call.")) return;
    try {
      await api.post(`/meetings/${m.meeting_id}/end`);
      toast.success("Meeting ended");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Could not end"); }
  };

  const join = async (m) => {
    // Always hit the backend `/join` endpoint first so attendance is logged,
    // then open the Google Meet link it returns in a new tab. Jitsi has been
    // fully retired — anything served back here is a real Meet URL.
    setJoiningId(m.meeting_id);
    try {
      const { data } = await api.get(`/meetings/${m.meeting_id}/join`);
      if (!data?.url) throw new Error("No meeting URL returned");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not open meeting");
    } finally {
      setJoiningId(null);
    }
  };

  const list = tab === "upcoming"
    ? meetings.filter((m) => m.status !== "ended")
    : history;

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <PageHeader title="Meetings" subtitle="Schedule Google Meet video calls — invitees get an email invite with the Meet link" icon={Video} accent="indigo"
          actions={
            <>
              <button onClick={load} className="btn-ghost" data-testid="meetings-sync"><RefreshCw className="w-4 h-4" /> Sync</button>
              <button onClick={() => setShow(true)} className="btn-primary" data-testid="meetings-new"><Plus className="w-4 h-4" /> New meeting</button>
            </>
          } />

        {/* Upcoming / History tabs */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab("upcoming")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "upcoming" ? "bg-orange-500 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid="meet-tab-upcoming">
            <Calendar className="w-4 h-4 inline mr-1.5" />Upcoming &amp; Active <span className="opacity-70">({meetings.filter((m) => m.status !== "ended").length})</span>
          </button>
          <button onClick={() => setTab("history")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "history" ? "bg-orange-500 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid="meet-tab-history">
            <History className="w-4 h-4 inline mr-1.5" />History <span className="opacity-70">({history.length})</span>
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {list.length === 0 && <div className="col-span-full dashboard-card p-12 text-center text-slate-400">No {tab === "upcoming" ? "active" : "past"} meetings.</div>}
          {list.map((m) => {
            const ended = m.status === "ended";
            const upcoming = !ended && m.scheduled_at && new Date(m.scheduled_at) > new Date();
            const canEnd = !ended && (m.scheduler_id === user.user_id || user.role === "admin");
            return (
              <div key={m.meeting_id} className={`dashboard-card p-5 ${upcoming ? "border-orange-200" : ended ? "opacity-80" : ""}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Video className="w-4 h-4 text-orange-600" />
                      <span className={`role-pill ${upcoming ? "bg-orange-100 text-orange-700" : ended ? "bg-slate-200 text-slate-600" : "bg-slate-100 text-slate-600"}`}>{ended ? "Ended" : upcoming ? "Upcoming" : "Past"}</span>
                      {m.meet_link && <span className="role-pill bg-sky-100 text-sky-700">Google Meet</span>}
                    </div>
                    <div className="font-display font-bold text-lg">{m.title}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {m.scheduled_at ? new Date(m.scheduled_at).toLocaleString() : "—"} · {m.duration_min}m</div>
                    <div className="text-xs text-slate-500 mt-1">By {m.scheduler_name} ({ROLE_NICE(m)})</div>
                    {m.description && <div className="text-sm text-slate-700 mt-2">{m.description}</div>}
                    <div className="text-xs text-slate-500 mt-2">With: {(m.participant_names || []).join(", ")}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  {!ended && (
                    <button onClick={() => join(m)} className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-1.5" data-testid={`join-${m.meeting_id}`}>
                      <ExternalLink className="w-4 h-4" /> {joiningId === m.meeting_id ? "Opening…" : "Join meeting"}
                    </button>
                  )}
                  {canEnd && (
                    <button onClick={() => end(m)} className="bg-rose-500 text-white text-sm rounded-full px-3 py-2 flex items-center gap-1.5 hover:bg-rose-600" data-testid={`end-${m.meeting_id}`}>
                      <Square className="w-3.5 h-3.5" /> End
                    </button>
                  )}
                  {ended && (m.participants || []).includes(user.user_id) && (
                    <button onClick={() => setRateFor(m)} className="btn-ghost py-2 text-sm flex items-center gap-1.5" data-testid={`rate-${m.meeting_id}`}>
                      <Star className="w-3.5 h-3.5" /> {myRated.has(m.meeting_id) ? "Edit my rating" : "Rate this call"}
                    </button>
                  )}
                  {!ended && (m.scheduler_id === user.user_id || user.role === "admin") && <button onClick={() => cancel(m)} className="text-rose-500 p-2" title="Cancel meeting"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <ModalCloseBtn onClose={() => setShow(false)} />
            <h3 className="font-display font-black text-2xl mb-1">Schedule meeting</h3>
            <p className="text-xs text-slate-500 mb-4">Pick attendees from your scoped team list below &mdash; they&apos;ll get an email invite.</p>
            <form onSubmit={submit} className="space-y-3">
              <input required placeholder="Title" className="input-pill" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meet-title" />
              <textarea rows={2} placeholder="Description" className="input-pill" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <input required type="datetime-local" className="input-pill" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="meet-date" />
              <input type="number" min={10} step={5} placeholder="Duration (min)" className="input-pill" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 30 })} />
              <div>
                <div className="text-xs text-slate-500 mb-2">Add participants</div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {eligible.map((u) => (
                    <label key={u.user_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer text-sm">
                      <input type="checkbox" checked={form.participants.includes(u.user_id)} onChange={() => togglePart(u.user_id)} />
                      <span className="font-semibold">{u.name}</span>
                      <span className="text-xs text-slate-500">· {ROLE_LABELS[u.role] || u.role}</span>
                    </label>
                  ))}
                  {eligible.length === 0 && <div className="text-xs text-slate-400 p-2">No eligible participants.</div>}
                </div>
              </div>
              <button type="submit" disabled={meetSubmit.submitting} className="btn-primary w-full py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="meet-submit">
                {meetSubmit.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Scheduling…</> : "Schedule & email invites"}
              </button>
            </form>
          </div>
        </div>
      )}

      {rateFor && (
        <RateMeetingModal
          meeting={rateFor}
          onClose={() => setRateFor(null)}
          onSubmitted={() => {
            const next = new Set(myRated); next.add(rateFor.meeting_id);
            setMyRated(next);
            localStorage.setItem("mtg_rated", JSON.stringify([...next]));
            setRateFor(null);
          }}
        />
      )}
    </Layout>
  );
}

function RateMeetingModal({ meeting, onClose, onSubmitted }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (stars < 1) { toast.error("Please pick a star rating"); return; }
    setBusy(true);
    try {
      await api.post(`/meetings/${meeting.meeting_id}/rate`, { stars, comment });
      toast.success("Thanks for your feedback!");
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not submit");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="rate-meeting-modal">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-display font-black text-xl">How was the call?</h3>
            <div className="mt-2 text-xs text-slate-500">Rating meeting:</div>
            <div className="text-sm font-bold text-slate-900" data-testid="rate-meeting-title">{meeting.title}</div>
            {meeting.scheduled_at && <div className="text-[11px] text-slate-400 mt-0.5">{new Date(meeting.scheduled_at).toLocaleString()}</div>}
          </div>
          <ModalCloseBtn onClose={onClose} />
        </div>
        <div className="flex items-center gap-1 my-5 justify-center">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setStars(n)}
              className="p-1 transition-transform hover:scale-110"
              data-testid={`rate-star-${n}`}
            >
              <Star className={`w-9 h-9 ${(hover || stars) >= n ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
            </button>
          ))}
        </div>
        <div className="relative">
          <MessageSquare className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Any feedback for the host? (optional)"
            rows={3}
            className="input-pill pl-9 pt-2.5 resize-none"
            data-testid="rate-comment"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost px-4 py-2" data-testid="rate-cancel">Skip</button>
          <button onClick={submit} disabled={busy || stars < 1} className="btn-primary px-5 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="rate-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} Submit rating
          </button>
        </div>
      </div>
    </div>
  );
}
